import React, { useEffect, useState } from "react";
import { Link, Redirect } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Form, Button, Spinner, Label } from "reactstrap";
import firebase from "../../../config/firebase";
import { isUserExist, register } from "../../../store/actions/authActions";

import Logo from "../../../assets/img/custom/logo.png";

import RegisterFormFields from "./RegisterFormFields";
import ManagerSection from "./ManagerSection";
import RegisterAgreements from "./RegisterAgreements";
import RegisterModals from "./RegisterModals";

function Register() {
  const dispatch = useDispatch();
  const location = useLocation();

  const urlParams = new URLSearchParams(location.search);
  const isManagerRedirect = urlParams.get("key") === "manager";

  // Basic fields
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [paypalName, setPaypalName] = useState("");
  const [password, setPassword] = useState("");
  const [veryPhoneNumber, setVeryPhoneNumber] = useState("");
  const [datOffBirth, setDatOffBirth] = useState("");
  const [dobChecked, setDobChecked] = useState(false);
  const [trainingChecked, setTrainingChecked] = useState(false);

  // Demographics (hidden from UI, defaults for Firestore document structure)
  const [gender, setGender] = useState("");
  const [education] = useState("N/A");
  const [familyStatus] = useState("N/A");
  const [income] = useState("N/A");

  // Agreements
  const [terms, setTerms] = useState(false);
  const [identity, setIdentity] = useState(false);
  const [copyItems, setCopyItems] = useState(false);
  const [fhpPolicy, setFhpPolicy] = useState(false);
  const [tac, setTac] = useState(false);

  // Manager
  const [managerData, setManagerData] = useState({
    isManager: isManagerRedirect,
    description: "",
    photo: "",
    managerOptIn: null,
  });
  const [managerRequirementsModal, setManagerRequirementsModal] = useState(false);

  const auth = useSelector((state) => state.auth);

  // ── Effects ────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (isManagerRedirect && !managerRequirementsModal) {
      setManagerKey();
      setManagerRequirementsModal(true);
      toast.info("Welcome! You've been redirected to become a manager. Please review the requirements below.");
    }
  }, [isManagerRedirect]);

  useEffect(() => {
    if (window.recaptchaVerifier) window.recaptchaVerifier.clear();
  }, []);

  // ── Handlers ───────────────────────────────────────────────────────────────

  const handleDateOfBirthCheck = (val) => {
    const today = new Date();
    const threeYearsAgo = new Date(today.getFullYear() - 3, today.getMonth(), today.getDate());
    if (new Date(val) > threeYearsAgo) {
      toast.warning("Date of Birth must be at least 3 years ago");
    } else {
      setDatOffBirth(val);
    }
  };

  const handleRegister = (e) => {
    e.preventDefault();
    const creds = {
      ...(managerData.isManager && {
        photo: managerData.photo,
        description: managerData.description,
        managerOptIn: managerData.managerOptIn,
      }),
      isManager: managerData.isManager,
      name,
      email,
      paypal_email: paypalName,
      phoneNumber: veryPhoneNumber,
      gender,
      state: "N/A",
      city: "N/A",
      school: [],
      dateOfBirth: datOffBirth,
      income,
      familyStatus,
      education,
      zipCode: "00000",
      terms,
      fanOpt: null,
      role: "fan",
      created_at: firebase.firestore.Timestamp.now(),
      lastLoggedIn: firebase.firestore.Timestamp.now(),
      registration_amount: 0,
      walletAddress: "",
      fhpPolicy,
      cryptoAgreement: false,
      secondaryPaypalEmail: "",
      fhpCoinsTerms: false,
    };
    dispatch(
      isUserExist(email, () => {
        dispatch(
          register(creds, password, { id: "free", purchase_units: [{ amount: { value: "0" } }] })
        );
      })
    );
  };

  // ── Autocomplete option configs ────────────────────────────────────────────

  const defaultGenderProps = {
    options: ["Male", "Female", "Decline to Answer"],
    getOptionLabel: (o) => o,
  };

  const isSubmitDisabled =
    !name || !email || !terms ||
    !password || !identity || !copyItems || !gender ||
    !datOffBirth || !veryPhoneNumber ||
    (managerData.isManager && (!managerData.photo || !managerData.description));

  if (auth.registered) return <Redirect to="/" />;

  return (
    <>
      <div className="text-center mt-5">
        <a href="https://fans-help-players-athlete.web.app/">
          <img src={Logo} alt="logo" className="logo" />
        </a>
      </div>

      <Form>
        <div className="auth-card">
          <h2 className="auth-heading">Fan Sign Up</h2>

          <RegisterFormFields
            name={name} setName={setName}
            email={email} setEmail={setEmail}
            datOffBirth={datOffBirth} onDateOfBirthChange={handleDateOfBirthCheck}
            dobChecked={dobChecked} setDobChecked={setDobChecked}
            trainingChecked={trainingChecked} setTrainingChecked={setTrainingChecked}
            paypalName={paypalName} setPaypalName={setPaypalName}
            password={password} setPassword={setPassword}
            veryPhoneNumber={veryPhoneNumber} setVeryPhoneNumber={setVeryPhoneNumber}
            gender={gender} setGender={setGender}
            defaultGenderProps={defaultGenderProps}
          />

          <ManagerSection
            managerData={managerData} setManagerData={setManagerData}
            isManagerRedirect={isManagerRedirect}
            managerRequirementsModal={managerRequirementsModal}
            setManagerRequirementsModal={setManagerRequirementsModal}
            email={email} setEmail={setEmail}
          />

          <RegisterAgreements
            fhpPolicy={fhpPolicy} setFhpPolicy={setFhpPolicy}
            terms={terms} setTerms={setTerms}
            setTac={setTac}
            identity={identity} setIdentity={setIdentity}
            copyItems={copyItems} setCopyItems={setCopyItems}
          />
        </div>

        <div className="text-center">
          <Button
            className="auth-button mt-5"
            disabled={isSubmitDisabled}
            onClick={handleRegister}
          >
            {auth.requested ? <Spinner size="sm" /> : "Register"}
          </Button>
        </div>
      </Form>

      <div className="mt-3 mx-0 mx-md-5 text-center">
        <Label className="auth-label mb-4">Already have an account?</Label>{" "}
        {isManagerRedirect ? (
          <Label className="auth-label text-muted"><b><u>Login</u></b></Label>
        ) : (
          <Link to="/auth/login">
            <Label className="text-primary auth-label"><b><u>Login</u></b></Label>
          </Link>
        )}
      </div>

      <RegisterModals tac={tac} setTac={setTac} />
    </>
  );
}

export default Register;
